import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { shallowEqual } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import {
  profileFormType,
  ParentEntityType,
  ProfileDocumentType,
  ProfileVerification,
  ProfileVerificationRow,
} from "../../../api/verificationsV2";
import { useAppDispatch, useAppSelector } from "../../../app/store";
import BaseDataTable, { IDataRow } from "../../../components/ui/baseDataTable";
import { ErrorDisplay } from "../../../components/ui/error/ErrorDisplay";
import { CustomLoadingSpinner } from "../../../components/ui/spinners/LoadingSpinner";
import { showErrorToast, showSuccessToast } from "../../../lib/toast";
import {
  SaveFooter,
  VerificationEmptyTableBody,
  VerificationTableSection,
} from "../../legalTechnicalAnalysis/components/VerificationContainer";
import { selectFormConfigEntities } from "../../legalTechnicalAnalysis/Logic/formConfigsSlice";
import {
  selectProfileVerifications,
  selectProfileVerificationsError,
  selectProfileVerificationsStatus,
} from "../Logic/verificationsSlice";
import {
  fetchProfileVerifications,
  upsertProfileVerifications,
} from "../Logic/verificationsThunk";

import { buildProfileVerificationColumns } from "./profileVerificationColumns";

interface ProfileVerificationsTableProps {
  parentEntityType: ParentEntityType;
  parentEntityId: string;
  stakeholderId: string;
}

const buildEmptyRow = (
  parentEntityType: ParentEntityType,
  parentEntityId: string,
  stakeholderId: string,
): ProfileVerificationRow => ({
  verification_id: uuidv4(),
  parent_entity_type: parentEntityType,
  parent_entity_id: parentEntityId,
  stakeholder_id: stakeholderId,
  document_type: undefined as unknown as ProfileDocumentType,
  onboarding_details: {},
  current_details: {},
  anomaly: undefined,
  isAdded: true,
  isDirty: true,
});

const ProfileVerificationsTable: React.FC<ProfileVerificationsTableProps> = ({
  parentEntityType,
  parentEntityId,
  stakeholderId,
}) => {
  const { id: caseId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();

  const [isSaving, setIsSaving] = useState(false);

  const status = useAppSelector(selectProfileVerificationsStatus);
  const error = useAppSelector(selectProfileVerificationsError);
  const formConfigs = useAppSelector(selectFormConfigEntities);

  const savedRows = useAppSelector(
    (state) =>
      selectProfileVerifications(
        state,
        parentEntityType,
        parentEntityId,
        stakeholderId,
      ),
    shallowEqual,
  );

  const [rows, setRows] = useState<ProfileVerificationRow[]>([]);

  useEffect(() => {
    setRows(savedRows.map((row) => ({ ...row, isAdded: false, isDirty: false })));
  }, [savedRows, parentEntityId, stakeholderId]);

  const hasChanges = useMemo(() => rows.some((row) => row.isDirty), [rows]);

  const isStatusFormComplete = (
    statusValues: Record<string, any> | null | undefined,
    documentType: ProfileDocumentType,
  ) => {
    const mandatoryFields = (
      formConfigs[profileFormType(documentType)]?.configs?.fields ?? []
    ).filter((field) => field.is_mandatory);

    return mandatoryFields.every((field) => {
      const value = (statusValues ?? {})[field.key];

      if (Array.isArray(value)) return value.length > 0;
      return value !== undefined && value !== null && value !== "";
    });
  };

  const isRowReadyToSave = (row: ProfileVerificationRow) =>
    Boolean(row.document_type) &&
    isStatusFormComplete(row.onboarding_details, row.document_type) &&
    isStatusFormComplete(row.current_details, row.document_type);

  const hasIncompleteRows = useMemo(
    () => rows.filter((row) => row.isDirty).some((row) => !isRowReadyToSave(row)),
    [rows, formConfigs],
  );

  const handleAddRow = () => {
    setRows((previous) => [
      ...previous,
      buildEmptyRow(parentEntityType, parentEntityId, stakeholderId),
    ]);
  };

  const handleDeleteRow = (rowIndex: number) => {
    setRows((previous) => previous.filter((_, index) => index !== rowIndex));
  };

  const handleRowChange = (
    rowIndex: number,
    value: any,
    field: keyof ProfileVerificationRow,
  ) => {
    setRows((previous) =>
      previous.map((row, index) => {
        if (index !== rowIndex) return row;

        if (field === "document_type") {
          return {
            ...row,
            document_type: value,
            onboarding_details: {},
            current_details: {},
            anomaly: undefined,
            isDirty: true,
          };
        }

        return { ...row, [field]: value, isDirty: true };
      }),
    );
  };

  const handleSave = async () => {
    if (!caseId) return;

    const dirtyRows = rows.filter((row) => row.isDirty);
    if (dirtyRows.length === 0) return;

    setIsSaving(true);

    try {
      const verifications: ProfileVerification[] = dirtyRows.map(
        ({ isAdded, isDirty, ...row }) => ({ ...row, anomaly: row.anomaly || null }),
      );

      await dispatch(upsertProfileVerifications({ caseId, verifications })).unwrap();

      showSuccessToast("Profile verifications saved successfully");
    } catch {
      showErrorToast("Failed to save verifications. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscardChanges = () => {
    setRows(savedRows.map((row) => ({ ...row, isAdded: false, isDirty: false })));
  };

  const handleRetry = () => {
    if (caseId) dispatch(fetchProfileVerifications(caseId));
  };

  const dataRows: IDataRow<ProfileVerificationRow>[] = useMemo(
    () =>
      rows.map((row) => ({
        data: row,
        isEditable: true,
        widgets: row.isAdded ? ["add", "delete"] : ["add"],
        onAdd: handleAddRow,
        onDelete: handleDeleteRow,
      })),
    [rows],
  );

  const isLoading = status === "loading" && !isSaving;

  return (
    <div className="flex w-full flex-col">
      <VerificationTableSection
        title="Profile Verifications"
      >
        {isLoading ? (
          <CustomLoadingSpinner
            title="Loading Verifications..."
            subtitle="Fetching profile verifications for this stakeholder."
            minHeight="min-h-[160px]"
          />
        ) : error ? (
          <ErrorDisplay
            error="Unable to load profile verifications. Please try again."
            onRetry={handleRetry}
          />
        ) : (
          <BaseDataTable<ProfileVerificationRow>
            columnConfigs={buildProfileVerificationColumns({
              rows,
              onRowChange: handleRowChange,
            })}
            dataRows={dataRows}
            showDefaultTableBody={dataRows.length === 0}
            defaultTableBody={
              <VerificationEmptyTableBody
                title="No profile verification records"
                subtitle="Add a document to record its onboarding and current status"
                onAdd={handleAddRow}
              />
            }
          />
        )}
      </VerificationTableSection>

      {hasChanges && (
        <SaveFooter
          isSaving={isSaving}
          disabled={hasIncompleteRows}
          onSave={handleSave}
          onDiscard={handleDiscardChanges}
        />
      )}
    </div>
  );
};

export default ProfileVerificationsTable;
